import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import { canViewOrgWideEmployeeBenefits, canRequestPeopleHubTimeOff } from '../utils/employeeBenefitsAccess';
import {
  buildInstallWeatherSitesFromInstallMap,
  buildProjectInstallsByYmd,
  listInstallYmdsFromMap,
  type PeopleCalProjectInstall,
} from '../utils/peopleHubProjectInstalls';
import {
  type CompanyHoliday,
  type EmployeeBenefitsSettingsDoc,
  type TimeOffRequest,
  type TimeOffType,
  createTimeOffRequest,
  createAdminCalendarTimeOff,
  deleteAdminCalendarTimeOff,
  externalCalendarPersonUserId,
  defaultEmployeeBenefitsSettings,
  deletePendingTimeOffRequest,
  parseCalendarYmd,
  subscribeEmployeeBenefitsSettings,
  subscribeOwnTimeOffRequests,
  subscribePendingTimeOffQueue,
  subscribeTeamVisibleTimeOff,
  updateTimeOffRequest,
} from '../utils/peopleHubFirestore';
import { listPayDatesInRange } from '../utils/leaveAccrual';
import { allDayCalendarDate } from '../utils/allDayDate';
import { Timestamp } from 'firebase/firestore';
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isWithinInterval,
  max,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { CalendarRange } from 'lucide-react';
import { launchPayrollConfetti } from '../components/people/PayrollConfettiBurst';
import { useInstallWeatherForecast } from '../hooks/useInstallWeatherForecast';
import InstallWeatherRainIcon from '../components/weather/InstallWeatherRainIcon';
import { filterYmdsWithinHorizon } from '../utils/installWeather';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

type CellTimeOffEntry = { request: TimeOffRequest; mode: 'full' | 'continuation'; hatchPhase: number };

type DayCell = {
  key: string;
  holidays: CompanyHoliday[];
  timeOff: CellTimeOffEntry[];
  projectInstalls: PeopleCalProjectInstall[];
  payrollPayDate?: boolean;
};

function toYmd(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function requestCoversDay(req: TimeOffRequest, day: Date): boolean {
  const start = allDayCalendarDate(req.startDate.toDate());
  const end = allDayCalendarDate(req.endDate.toDate());
  return isWithinInterval(day, { start, end });
}

/** Short label for calendar cell (e.g. #0057); full name stays in day hover flyout. */
function projectInstallChipLabel(projectName: string): string {
  const t = projectName.trim();
  const hash = /#\s*(\d+)/.exec(t);
  if (hash) return `#${hash[1]}`;
  const leadNum = /^(\d{3,6})\b/.exec(t);
  if (leadNum) return `#${leadNum[1]}`;
  return t.length <= 7 ? t : `${t.slice(0, 6)}…`;
}

function emptyDayCell(key: string): DayCell {
  return { key, holidays: [], timeOff: [], projectInstalls: [], payrollPayDate: false };
}

type CalDayHoverState = {
  ymd: string;
  date: Date;
  cell: DayCell;
  x: number;
  y: number;
};

/** Hover flyout only for days with something on the calendar (not blank cells). */
function dayCellHasCalendarFlyout(cell: DayCell): boolean {
  return (
    cell.payrollPayDate === true ||
    cell.holidays.length > 0 ||
    cell.projectInstalls.length > 0 ||
    cell.timeOff.length > 0
  );
}

/**
 * Keep the flyout near the pointer. When there is not enough room below, place it above the
 * cursor so `top` still tracks vertical movement (fixed `top` clamp was pinning Y for whole cells).
 */
function clampFlyoutPosition(x: number, y: number, flyoutW: number, reserveH: number): { left: number; top: number } {
  const pad = 10;
  const margin = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600;
  const h = Math.min(reserveH, Math.max(120, vh - pad * 2 - margin * 2));

  let left = x + margin;
  if (left + flyoutW + pad > vw) left = Math.max(pad, vw - flyoutW - pad);

  let top = y + margin;
  if (top + h + pad > vh) {
    top = y - h - margin;
  }
  if (top < pad) top = pad;
  if (top + h + pad > vh) top = Math.max(pad, vh - h - pad);

  return { left, top };
}

function timeOffTypeLabel(t: TimeOffType): string {
  if (t === 'vacation') return 'Vacation / PTO';
  if (t === 'ooo') return 'Out of office';
  return 'Sick';
}

function PeopleCalDayFlyout({
  state,
  getInstallWeatherLine,
}: {
  state: CalDayHoverState | null;
  getInstallWeatherLine?: (projectId: string, ymd: string) => string | undefined;
}) {
  if (!state || typeof document === 'undefined') return null;
  if (!dayCellHasCalendarFlyout(state.cell)) return null;

  const reserveH =
    typeof window !== 'undefined' ? Math.min(240, Math.max(140, Math.floor(window.innerHeight * 0.34))) : 200;
  const { left, top } = clampFlyoutPosition(state.x, state.y, 292, reserveH);
  const c = state.cell;

  return createPortal(
    <div className="people-cal-day-flyout" style={{ left, top }} role="tooltip">
      <div className="people-cal-day-flyout-title">{format(state.date, 'EEEE, MMMM d, yyyy')}</div>

      {c.payrollPayDate ? (
        <section className="people-cal-day-flyout-section">
          <h4 className="people-cal-day-flyout-h">Payroll</h4>
          <p className="people-cal-day-flyout-line">Payroll pay date</p>
        </section>
      ) : null}

      {c.holidays.length > 0 ? (
        <section className="people-cal-day-flyout-section">
          <h4 className="people-cal-day-flyout-h">Company holidays</h4>
          <ul className="people-cal-day-flyout-list">
            {c.holidays.map(hol => (
              <li key={hol.id}>{hol.name}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {c.projectInstalls.length > 0 ? (
        <section className="people-cal-day-flyout-section">
          <h4 className="people-cal-day-flyout-h">Project installs</h4>
          <ul className="people-cal-day-flyout-list">
            {c.projectInstalls.map(ins => {
              const weatherLine = getInstallWeatherLine?.(ins.projectId, state.ymd);
              return (
                <li key={ins.taskId}>
                  <span className="people-cal-day-flyout-strong">{ins.projectName}</span>
                  <span className="people-cal-day-flyout-sub"> — {ins.taskTitle}</span>
                  {weatherLine ? (
                    <div className="people-cal-day-flyout-weather">{weatherLine}</div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {c.timeOff.length > 0 ? (
        <section className="people-cal-day-flyout-section">
          <h4 className="people-cal-day-flyout-h">Time off</h4>
          <ul className="people-cal-day-flyout-list">
            {c.timeOff.map(({ request: r, mode }) => {
              const rangeLabel = `${format(allDayCalendarDate(r.startDate.toDate()), 'MMM d')} – ${format(allDayCalendarDate(r.endDate.toDate()), 'MMM d, yyyy')}`;
              return (
                <li key={`${r.id}-${mode}`}>
                  <span className="people-cal-day-flyout-strong">{r.displayName}</span>
                  <span className="people-cal-day-flyout-sub">
                    {' '}
                    · {timeOffTypeLabel(r.type)}
                    {mode === 'continuation' ? ' (continued)' : ''}
                  </span>
                  <div className="people-cal-day-flyout-sub">{rangeLabel}</div>
                  {r.notes?.trim() ? <div className="people-cal-day-flyout-notes">{r.notes.trim()}</div> : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>,
    document.body,
  );
}

const PeopleHubPage: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { crmUsers, enhancedDeals, timberProjects, projectTasks } = useCRM();
  useCRMCollections(['projectTasks']);
  const [settings, setSettings] = useState<EmployeeBenefitsSettingsDoc>(defaultEmployeeBenefitsSettings());
  const [teamTimeOff, setTeamTimeOff] = useState<TimeOffRequest[]>([]);
  const [ownTimeOff, setOwnTimeOff] = useState<TimeOffRequest[]>([]);
  const [pendingQueue, setPendingQueue] = useState<TimeOffRequest[]>([]);
  const [rangeStart, setRangeStart] = useState(() => startOfMonth(new Date()));
  const [monthsVisible, setMonthsVisible] = useState<2 | 3 | 6>(3);
  const [personFilter, setPersonFilter] = useState<string>('');
  const [pendingListenerError, setPendingListenerError] = useState<string | null>(null);
  const [formStart, setFormStart] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [formEnd, setFormEnd] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [formType, setFormType] = useState<TimeOffType>('vacation');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [adminCalPerson, setAdminCalPerson] = useState('');
  const [adminCalExternalName, setAdminCalExternalName] = useState('');
  const [adminCalStart, setAdminCalStart] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [adminCalEnd, setAdminCalEnd] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [adminCalType, setAdminCalType] = useState<TimeOffType>('vacation');
  const [adminCalNotes, setAdminCalNotes] = useState('');
  const [adminCalSubmitting, setAdminCalSubmitting] = useState(false);

  const todayPayrollFlagRef = useRef<HTMLDivElement | null>(null);
  const lastPayrollConfettiSigRef = useRef('');
  const calHoverMoveRaf = useRef<number | null>(null);
  const calHoverTrackedElRef = useRef<HTMLDivElement | null>(null);
  const calHoverTrackedYmdRef = useRef<string | null>(null);
  const calHoverDocCleanupRef = useRef<(() => void) | null>(null);

  const [calDayHover, setCalDayHover] = useState<CalDayHoverState | null>(null);

  const endCalDayHoverTracking = useCallback(() => {
    if (calHoverMoveRaf.current != null) {
      cancelAnimationFrame(calHoverMoveRaf.current);
      calHoverMoveRaf.current = null;
    }
    calHoverDocCleanupRef.current?.();
    calHoverDocCleanupRef.current = null;
    calHoverTrackedElRef.current = null;
    calHoverTrackedYmdRef.current = null;
  }, []);

  useEffect(() => () => endCalDayHoverTracking(), [endCalDayHoverTracking]);

  const me = useMemo(() => crmUsers.find(u => u.uid === user?.id), [crmUsers, user?.id]);
  const approvalDesignations = me?.designations ?? user?.designations;
  const canOrg = useMemo(
    () => canViewOrgWideEmployeeBenefits({ authRole: user?.role, designations: approvalDesignations }),
    [user?.role, approvalDesignations],
  );
  const canRequestTimeOff = useMemo(
    () => canRequestPeopleHubTimeOff(user?.role, user?.designations),
    [user?.role, user?.designations],
  );

  useEffect(() => {
    const unsub = subscribeEmployeeBenefitsSettings(
      setSettings,
      e => console.error('[PeopleHub] settings', e),
    );
    return unsub;
  }, []);

  useEffect(() => {
    const m = settings.peopleHubDefaultCalendarMonthsVisible;
    if (m === 2 || m === 3 || m === 6) setMonthsVisible(m);
  }, [settings.peopleHubDefaultCalendarMonthsVisible]);

  useEffect(() => {
    const unsub = subscribeTeamVisibleTimeOff(setTeamTimeOff, e => console.error('[PeopleHub] team', e));
    return unsub;
  }, []);

  useEffect(() => {
    if (!user?.id || !canRequestTimeOff) return;
    const unsub = subscribeOwnTimeOffRequests(user.id, setOwnTimeOff, e =>
      console.error('[PeopleHub] own', e),
    );
    return unsub;
  }, [user?.id, canRequestTimeOff]);

  useEffect(() => {
    if (!canOrg) {
      setPendingQueue([]);
      setPendingListenerError(null);
      return;
    }
    setPendingListenerError(null);
    const unsub = subscribePendingTimeOffQueue(
      rows => {
        setPendingQueue(rows);
        setPendingListenerError(null);
      },
      e => {
        console.error('[PeopleHub] pending queue listener', e);
        setPendingListenerError(e.message || String(e));
      },
    );
    return unsub;
  }, [canOrg]);

  const dealById = useMemo(() => {
    const m = new Map<string, (typeof enhancedDeals)[number]>();
    for (const d of enhancedDeals) {
      if (d.id) m.set(d.id, d);
    }
    return m;
  }, [enhancedDeals]);

  const visibleMonths = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < monthsVisible; i++) out.push(addMonths(rangeStart, i));
    return out;
  }, [rangeStart, monthsVisible]);

  const installsByYmd = useMemo(
    () => buildProjectInstallsByYmd(dealById, timberProjects, projectTasks, rangeStart, monthsVisible),
    [dealById, timberProjects, projectTasks, rangeStart, monthsVisible],
  );

  const installWeatherSites = useMemo(
    () => buildInstallWeatherSitesFromInstallMap(installsByYmd, timberProjects),
    [installsByYmd, timberProjects],
  );

  const installWeatherDateYmds = useMemo(
    () => filterYmdsWithinHorizon(listInstallYmdsFromMap(installsByYmd)),
    [installsByYmd],
  );

  const installWeather = useInstallWeatherForecast({
    sites: installWeatherSites,
    dateYmds: installWeatherDateYmds,
    enabled: installWeatherDateYmds.length > 0 && installWeatherSites.length > 0,
  });

  const getInstallWeatherLine = useCallback(
    (projectId: string, ymd: string): string | undefined => {
      if (!installWeather.dayHasRain(ymd, projectId)) return undefined;
      const day = installWeather.getDayWeather(ymd, projectId);
      return day ? installWeather.formatDayLine(day) : undefined;
    },
    [installWeather],
  );

  const dayMap: Map<string, DayCell> = useMemo(() => {
    const map = new Map<string, DayCell>();
    const rangeEnd = endOfMonth(addMonths(rangeStart, monthsVisible - 1));
    const rangeStart0 = startOfDay(rangeStart);

    const ensure = (key: string): DayCell => {
      let c = map.get(key);
      if (!c) {
        c = { key, holidays: [], timeOff: [], projectInstalls: [], payrollPayDate: false };
        map.set(key, c);
      }
      return c;
    };

    if (settings.payrollSchedule) {
      const payYm = listPayDatesInRange(settings.payrollSchedule, rangeStart0, rangeEnd);
      for (const ymd of payYm) {
        ensure(ymd).payrollPayDate = true;
      }
    }

    for (const h of settings.holidays) {
      try {
        const d = parseCalendarYmd(h.date);
        if (d >= startOfDay(rangeStart) && d <= rangeEnd) {
          ensure(toYmd(d)).holidays.push(h);
        }
      } catch {
        /* ignore bad dates */
      }
    }

    const reqs = teamTimeOff.filter(r => {
      if (personFilter && r.userId !== personFilter) return false;
      return true;
    });

    eachDayOfInterval({ start: rangeStart0, end: rangeEnd }).forEach(day => {
      const key = toYmd(day);
      const matches: CellTimeOffEntry[] = [];
      for (const r of reqs) {
        if (!requestCoversDay(r, day)) continue;
        const reqStart = allDayCalendarDate(r.startDate.toDate());
        const labelAnchor = max([reqStart, rangeStart0]);
        const mode: CellTimeOffEntry['mode'] = isSameDay(day, labelAnchor) ? 'full' : 'continuation';
        const hatchPhase = Math.max(0, differenceInCalendarDays(startOfDay(day), reqStart));
        matches.push({ request: r, mode, hatchPhase });
      }
      matches.sort((a, b) => {
        const am = a.request.startDate.toMillis();
        const bm = b.request.startDate.toMillis();
        if (am !== bm) return am - bm;
        return a.request.id.localeCompare(b.request.id);
      });
      for (const m of matches) {
        ensure(key).timeOff.push(m);
      }
    });

    installsByYmd.forEach((installs, ymd) => {
      ensure(ymd).projectInstalls = installs;
    });

    return map;
  }, [
    settings.holidays,
    settings.payrollSchedule,
    teamTimeOff,
    rangeStart,
    monthsVisible,
    personFilter,
    installsByYmd,
  ]);

  const calendarPeopleOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const u of crmUsers) {
      if (!u.isActive) continue;
      byId.set(u.uid, u.displayName || u.email || u.uid);
    }
    for (const r of teamTimeOff) {
      if (!byId.has(r.userId)) {
        byId.set(r.userId, r.displayName || r.userId);
      }
    }
    return Array.from(byId.entries())
      .map(([uid, label]) => ({ uid, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [crmUsers, teamTimeOff]);

  const adminCalendarPersonOptions = useMemo(
    () =>
      crmUsers
        .filter(u => u.isActive)
        .map(u => ({ uid: u.uid, label: u.displayName || u.email || u.uid }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [crmUsers],
  );

  const adminCalendarEntries = useMemo(
    () =>
      teamTimeOff
        .filter(r => r.calendarOnly === true)
        .sort((a, b) => b.startDate.toMillis() - a.startDate.toMillis()),
    [teamTimeOff],
  );

  const todayYmd = toYmd(startOfDay(new Date()));

  const todayInVisiblePayroll = useMemo(() => {
    const rangeEnd = endOfMonth(addMonths(rangeStart, monthsVisible - 1));
    const rangeStart0 = startOfDay(rangeStart);
    const todayStart = startOfDay(new Date());
    if (!isWithinInterval(todayStart, { start: rangeStart0, end: rangeEnd })) return false;
    return dayMap.get(toYmd(todayStart))?.payrollPayDate === true;
  }, [rangeStart, monthsVisible, dayMap]);

  useLayoutEffect(() => {
    if (!user) return;
    if (location.pathname !== '/people') {
      lastPayrollConfettiSigRef.current = '';
      return;
    }
    if (!todayInVisiblePayroll) return;
    const scheduleSig = settings.payrollSchedule ? JSON.stringify(settings.payrollSchedule) : '';
    const sig = `${location.key}|${todayYmd}|${scheduleSig}|v1`;
    if (lastPayrollConfettiSigRef.current === sig) return;
    const el = todayPayrollFlagRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    launchPayrollConfetti({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    lastPayrollConfettiSigRef.current = sig;
  }, [user, location.pathname, location.key, todayYmd, todayInVisiblePayroll, settings.payrollSchedule]);

  if (!user) return null;

  const displayName = me?.displayName || user.name || user.email || 'Me';

  const submitRequest = async () => {
    if (!user.id) return;
    const s = parseCalendarYmd(formStart);
    const e = parseCalendarYmd(formEnd);
    if (e < s) {
      alert('End date must be on or after the start date.');
      return;
    }
    setSubmitting(true);
    try {
      await createTimeOffRequest({
        userId: user.id,
        displayName,
        start: s,
        end: e,
        type: formType,
        notes: formNotes,
      });
      setFormNotes('');
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdminCalendarEntry = async () => {
    if (!user.id) return;
    const s = parseCalendarYmd(adminCalStart);
    const e = parseCalendarYmd(adminCalEnd);
    if (e < s) {
      alert('End date must be on or after the start date.');
      return;
    }

    let userId = '';
    let personName = '';
    if (adminCalPerson === '__external__') {
      personName = adminCalExternalName.trim();
      if (!personName) {
        alert('Enter a name for this person.');
        return;
      }
      userId = externalCalendarPersonUserId();
    } else if (adminCalPerson) {
      userId = adminCalPerson;
      const picked = crmUsers.find(u => u.uid === adminCalPerson);
      personName = picked?.displayName || picked?.email || adminCalPerson;
    } else {
      alert('Choose who this time off is for.');
      return;
    }

    setAdminCalSubmitting(true);
    try {
      await createAdminCalendarTimeOff({
        userId,
        displayName: personName,
        start: s,
        end: e,
        type: adminCalType,
        notes: adminCalNotes,
        createdByUserId: user.id,
        createdByName: displayName,
      });
      setAdminCalNotes('');
      setAdminCalExternalName('');
      setAdminCalPerson('');
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not add to calendar');
    } finally {
      setAdminCalSubmitting(false);
    }
  };

  const removeAdminCalendarEntry = async (row: TimeOffRequest) => {
    if (!row.calendarOnly || !row.id) return;
    if (!window.confirm(`Remove ${row.displayName}'s calendar entry (${format(allDayCalendarDate(row.startDate.toDate()), 'MMM d')} – ${format(allDayCalendarDate(row.endDate.toDate()), 'MMM d, yyyy')})?`)) {
      return;
    }
    try {
      await deleteAdminCalendarTimeOff(row.id);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not remove entry');
    }
  };

  const approve = async (row: TimeOffRequest, decision: 'approved' | 'rejected') => {
    const now = Timestamp.now();
    const showOnTeamCalendar = decision === 'approved' && row.type !== 'sick';
    console.info('[PeopleHub][approve] submitting', {
      requestId: row.id,
      decision,
      authRole: user?.role,
      reviewerUid: user?.id,
    });
    try {
      await updateTimeOffRequest(row.id, {
        status: decision,
        showOnTeamCalendar: decision === 'approved' ? showOnTeamCalendar : false,
        reviewedByUserId: user.id,
        reviewedByName: displayName,
        reviewedAt: now,
        updatedAt: now,
      });
      console.info('[PeopleHub][approve] firestore update ok', { requestId: row.id, decision });
    } catch (e) {
      console.error('[PeopleHub][approve] firestore update failed', {
        requestId: row.id,
        decision,
        error: e,
      });
      alert(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const cancelOwnPending = async (row: TimeOffRequest) => {
    if (row.status !== 'pending') return;
    try {
      await updateTimeOffRequest(row.id, {
        status: 'cancelled',
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : 'Could not cancel');
    }
  };

  return (
    <div className="people-hub-route-root">
      <div className="ops-hub-toolbar">
            <div className="ops-hub-toolbar-left">
              <div>
                <h2 className="ops-hub-title">Team calendar</h2>
              </div>
            </div>
            <div className="ops-hub-toolbar-right">
              <div className="people-hub-toolbar-filters">
                <div className="people-hub-toolbar-field">
                  <span className="ops-modal-label">Calendar width</span>
                  <div className="ops-hub-views" role="group" aria-label="Months visible">
                    {([2, 3, 6] as const).map(n => (
                      <button
                        key={n}
                        type="button"
                        className={`ops-view-btn ${monthsVisible === n ? 'active' : ''}`}
                        aria-pressed={monthsVisible === n}
                        onClick={() => setMonthsVisible(n)}
                      >
                        {n} mo
                      </button>
                    ))}
                  </div>
                </div>
                {canOrg && (
                  <div className="people-hub-toolbar-field people-hub-field-grow">
                    <label className="ops-modal-label" htmlFor="people-person-filter">
                      Filter
                    </label>
                    <select
                      id="people-person-filter"
                      className="ops-modal-select people-hub-select-wide"
                      value={personFilter}
                      onChange={e => setPersonFilter(e.target.value)}
                    >
                      <option value="">Everyone</option>
                      {calendarPeopleOptions.map(o => (
                        <option key={o.uid} value={o.uid}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="people-hub-toolbar-field">
                  <span className="ops-modal-label">Navigate</span>
                  <div className="people-hub-range-group">
                    <button
                      type="button"
                      className="ops-hide-completed-btn"
                      onClick={() => setRangeStart(addMonths(rangeStart, -1))}
                    >
                      Earlier
                    </button>
                    <button
                      type="button"
                      className="ops-hide-completed-btn"
                      onClick={() => setRangeStart(startOfMonth(new Date()))}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className="ops-hide-completed-btn"
                      onClick={() => setRangeStart(addMonths(rangeStart, 1))}
                    >
                      Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="people-hub-body pm-scroll">
            <div className="people-hub-panel people-cal-panel">
              <div className="people-cal-legend">
                <span className="people-cal-legend-item people-cal-leg-holiday" title="Company holiday">
                  Holiday
                </span>
                <span className="people-cal-legend-item people-cal-leg-vacation" title="Vacation / PTO">
                  PTO
                </span>
                <span className="people-cal-legend-item people-cal-leg-ooo" title="Out of office">
                  OOO
                </span>
                <span className="people-cal-legend-item people-cal-leg-install" title="Project install">
                  Install
                </span>
                <span className="people-cal-legend-item people-cal-leg-rain" title="Rain in 10-day forecast">
                  <InstallWeatherRainIcon size={10} className="people-cal-leg-rain-icon" />
                  Rain forecast
                </span>
                <span className="people-cal-legend-item people-cal-leg-payroll" title="Payroll pay date">
                  Payroll
                </span>
                <span className="people-cal-legend-item people-cal-leg-today">Today</span>
              </div>
              <div className="people-cal-months">
                {visibleMonths.map(monthDate => (
                  <div key={format(monthDate, 'yyyy-MM')} className="people-cal-month-block">
                    <h3 className="people-cal-month-title">{format(monthDate, 'MMMM yyyy')}</h3>
                    <div className="people-cal-dow-row">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                        <div key={d} className="people-cal-dow-cell">
                          {d}
                        </div>
                      ))}
                    </div>
                    <div className="people-cal-days-grid">
                      {(() => {
                        const start = startOfMonth(monthDate);
                        const end = endOfMonth(monthDate);
                        const pad = start.getDay();
                        const cells: React.ReactNode[] = [];
                        for (let i = 0; i < pad; i++) {
                          cells.push(<div key={`pad-${i}`} className="people-cal-day people-cal-day-pad" />);
                        }
                        eachDayOfInterval({ start, end }).forEach(day => {
                          const key = toYmd(day);
                          const fullCell = dayMap.get(key) ?? emptyDayCell(key);
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          const isToday = key === todayYmd;
                          cells.push(
                            <div
                              key={key}
                              className={['people-cal-day', isWeekend && 'people-cal-day-weekend', isToday && 'people-cal-day-today']
                                .filter(Boolean)
                                .join(' ')}
                              {...(isToday ? ({ 'aria-current': 'date' } as const) : {})}
                              onMouseEnter={e => {
                                endCalDayHoverTracking();
                                if (!dayCellHasCalendarFlyout(fullCell)) return;

                                const el = e.currentTarget as HTMLDivElement;
                                calHoverTrackedElRef.current = el;
                                calHoverTrackedYmdRef.current = key;

                                let pendingX = e.clientX;
                                let pendingY = e.clientY;

                                const onDocMouseMove = (ev: MouseEvent) => {
                                  const trackEl = calHoverTrackedElRef.current;
                                  const trackYmd = calHoverTrackedYmdRef.current;
                                  if (!trackEl || trackYmd !== key) return;

                                  const r = trackEl.getBoundingClientRect();
                                  if (
                                    ev.clientX < r.left ||
                                    ev.clientX >= r.right ||
                                    ev.clientY < r.top ||
                                    ev.clientY >= r.bottom
                                  ) {
                                    const leaving = calHoverTrackedYmdRef.current;
                                    endCalDayHoverTracking();
                                    setCalDayHover(prev => (prev?.ymd === leaving ? null : prev));
                                    return;
                                  }

                                  pendingX = ev.clientX;
                                  pendingY = ev.clientY;
                                  if (calHoverMoveRaf.current != null) return;
                                  calHoverMoveRaf.current = requestAnimationFrame(() => {
                                    calHoverMoveRaf.current = null;
                                    const ymd = calHoverTrackedYmdRef.current;
                                    setCalDayHover(prev =>
                                      prev && ymd && prev.ymd === ymd
                                        ? { ...prev, x: pendingX, y: pendingY }
                                        : prev,
                                    );
                                  });
                                };

                                document.addEventListener('mousemove', onDocMouseMove, { passive: true });
                                calHoverDocCleanupRef.current = () => {
                                  document.removeEventListener('mousemove', onDocMouseMove);
                                };

                                setCalDayHover({
                                  ymd: key,
                                  date: day,
                                  cell: fullCell,
                                  x: e.clientX,
                                  y: e.clientY,
                                });
                              }}
                              onMouseLeave={() => {
                                if (calHoverTrackedYmdRef.current === key) {
                                  endCalDayHoverTracking();
                                  setCalDayHover(prev => (prev?.ymd === key ? null : prev));
                                }
                              }}
                            >
                              <div className="people-cal-day-num">{format(day, 'd')}</div>
                              <div className="people-cal-day-body">
                                {fullCell.payrollPayDate && (
                                  <div
                                    ref={isToday && fullCell.payrollPayDate ? todayPayrollFlagRef : undefined}
                                    className="people-cal-payroll-flag"
                                    data-people-payroll-flag="true"
                                    data-calendar-ymd={key}
                                    title="Payroll pay date"
                                    aria-label="Payroll pay date"
                                  >
                                    $
                                  </div>
                                )}
                                {fullCell.holidays.map(hol => (
                                  <div key={hol.id} className="people-cal-chip people-cal-chip-holiday people-cal-chip-cal">
                                    {hol.name}
                                  </div>
                                ))}
                                {fullCell.projectInstalls.map(ins => {
                                  const weatherLine = getInstallWeatherLine(ins.projectId, key);
                                  const title = weatherLine
                                    ? `${ins.projectName} — ${ins.taskTitle}\n${weatherLine}`
                                    : `${ins.projectName} — ${ins.taskTitle}`;
                                  const showRain = !!weatherLine;
                                  return (
                                    <Link
                                      key={ins.taskId}
                                      to={`/project-hub/${ins.projectId}`}
                                      className="people-cal-chip people-cal-chip-install people-cal-chip-cal"
                                      title={title}
                                    >
                                      {showRain && (
                                        <InstallWeatherRainIcon
                                          className="people-cal-chip-rain"
                                          title={weatherLine}
                                          size={10}
                                        />
                                      )}
                                      <span className="people-cal-chip-install-label">
                                        {projectInstallChipLabel(ins.projectName)}
                                      </span>
                                    </Link>
                                  );
                                })}
                                {fullCell.timeOff.map(({ request: r, mode, hatchPhase }) => {
                                  const typeClass =
                                    r.type === 'ooo' ? 'people-cal-chip-ooo' : 'people-cal-chip-vacation';
                                  const useHatch = r.type === 'vacation' || r.type === 'ooo';
                                  const hatchStyle = useHatch
                                    ? ({ ['--people-cal-hatch' as string]: String(hatchPhase) } as React.CSSProperties)
                                    : undefined;
                                  const rangeLabel = `${format(allDayCalendarDate(r.startDate.toDate()), 'MMM d')} – ${format(allDayCalendarDate(r.endDate.toDate()), 'MMM d, yyyy')}`;
                                  const title = `${r.displayName} — ${r.type} (${rangeLabel})`;
                                  const hatchClass = useHatch ? ' people-cal-chip-hatched' : '';
                                  if (mode === 'continuation') {
                                    return (
                                      <div
                                        key={`${r.id}-cont`}
                                        className={`people-cal-chip people-cal-timeoff-continuation ${typeClass}${hatchClass}`}
                                        style={hatchStyle}
                                        title={title}
                                        aria-label={`${r.displayName}, ${r.type}, continued (${rangeLabel})`}
                                      />
                                    );
                                  }
                                  return (
                                    <div
                                      key={r.id}
                                      className={`people-cal-chip ${typeClass} people-cal-chip-cal${hatchClass}`}
                                      style={hatchStyle}
                                      title={title}
                                    >
                                      {r.displayName}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>,
                          );
                        });
                        return cells;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {canRequestTimeOff && (
            <div className="people-hub-panel people-hub-timeoff-panel">
                <h3 className="people-hub-panel-title">
                  <CalendarRange size={16} aria-hidden /> Request time off
                </h3>
                <div className="ops-modal-body people-hub-form">
                  <div className="ops-modal-grid">
                    <div className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor="people-to-start">
                        Start
                      </label>
                      <input
                        id="people-to-start"
                        className="ops-modal-date"
                        type="date"
                        value={formStart}
                        onChange={e => setFormStart(e.target.value)}
                      />
                    </div>
                    <div className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor="people-to-end">
                        End
                      </label>
                      <input
                        id="people-to-end"
                        className="ops-modal-date"
                        type="date"
                        value={formEnd}
                        onChange={e => setFormEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ops-modal-row">
                    <label className="ops-modal-label" htmlFor="people-to-type">
                      Type
                    </label>
                    <select
                      id="people-to-type"
                      className="ops-modal-select"
                      value={formType}
                      onChange={e => setFormType(e.target.value as TimeOffType)}
                    >
                      <option value="vacation">Vacation / PTO</option>
                      <option value="sick">Sick</option>
                      <option value="ooo">Out of office</option>
                    </select>
                  </div>
                  <div className="ops-modal-section">
                    <label className="ops-modal-label" htmlFor="people-to-notes">
                      Notes (optional)
                    </label>
                    <textarea
                      id="people-to-notes"
                      className="ops-modal-textarea"
                      rows={2}
                      value={formNotes}
                      onChange={e => setFormNotes(e.target.value)}
                    />
                  </div>
                  <div className="people-hub-form-actions">
                    <button
                      type="button"
                      className="pm-btn pm-btn-primary"
                      disabled={submitting}
                      onClick={() => void submitRequest()}
                    >
                      {submitting ? 'Submitting…' : 'Submit request'}
                    </button>
                  </div>
                </div>

                <h4 className="people-hub-subpanel-title">My requests</h4>
                <ul className="people-hub-list">
                  {ownTimeOff.map(r => (
                    <li key={r.id} className="people-hub-list-item">
                      <div>
                        <strong>
                          {format(allDayCalendarDate(r.startDate.toDate()), 'MMM d')} –{' '}
                          {format(allDayCalendarDate(r.endDate.toDate()), 'MMM d, yyyy')}
                        </strong>{' '}
                        <span className="people-hub-muted">{r.type}</span> ·{' '}
                        <span className={`people-hub-status people-hub-status-${r.status}`}>{r.status}</span>
                      </div>
                      <div className="people-hub-list-actions">
                        {r.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              className="pm-btn pm-btn-ghost pm-btn-sm"
                              onClick={() => void cancelOwnPending(r)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="pm-btn pm-btn-ghost pm-btn-sm"
                              onClick={() => void deletePendingTimeOffRequest(r.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canOrg && (
              <div className="people-hub-panel people-hub-timeoff-panel">
                <h3 className="people-hub-panel-title">
                  <CalendarRange size={16} aria-hidden /> Add to team calendar
                </h3>
                <p className="people-hub-muted people-hub-tight-top">
                  Post approved time off for anyone — employees, architects, engineers, or contractors who
                  aren&apos;t in the employee list. These appear on the calendar immediately (no approval queue).
                </p>
                <div className="ops-modal-body people-hub-form">
                  <div className="ops-modal-row">
                    <label className="ops-modal-label" htmlFor="people-admin-cal-person">
                      Person
                    </label>
                    <select
                      id="people-admin-cal-person"
                      className="ops-modal-select"
                      value={adminCalPerson}
                      onChange={e => setAdminCalPerson(e.target.value)}
                    >
                      <option value="">Choose person…</option>
                      {adminCalendarPersonOptions.map(o => (
                        <option key={o.uid} value={o.uid}>
                          {o.label}
                        </option>
                      ))}
                      <option value="__external__">Other (contractor / guest)…</option>
                    </select>
                  </div>
                  {adminCalPerson === '__external__' && (
                    <div className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor="people-admin-cal-external">
                        Name
                      </label>
                      <input
                        id="people-admin-cal-external"
                        className="ops-modal-input"
                        type="text"
                        value={adminCalExternalName}
                        onChange={e => setAdminCalExternalName(e.target.value)}
                        placeholder="e.g. Jane Smith (contractor)"
                      />
                    </div>
                  )}
                  <div className="ops-modal-grid">
                    <div className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor="people-admin-cal-start">
                        Start
                      </label>
                      <input
                        id="people-admin-cal-start"
                        className="ops-modal-date"
                        type="date"
                        value={adminCalStart}
                        onChange={e => setAdminCalStart(e.target.value)}
                      />
                    </div>
                    <div className="ops-modal-row">
                      <label className="ops-modal-label" htmlFor="people-admin-cal-end">
                        End
                      </label>
                      <input
                        id="people-admin-cal-end"
                        className="ops-modal-date"
                        type="date"
                        value={adminCalEnd}
                        onChange={e => setAdminCalEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ops-modal-row">
                    <label className="ops-modal-label" htmlFor="people-admin-cal-type">
                      Type
                    </label>
                    <select
                      id="people-admin-cal-type"
                      className="ops-modal-select"
                      value={adminCalType}
                      onChange={e => setAdminCalType(e.target.value as TimeOffType)}
                    >
                      <option value="vacation">Vacation / PTO</option>
                      <option value="ooo">Out of office</option>
                      <option value="sick">Sick (hidden from team calendar)</option>
                    </select>
                  </div>
                  <div className="ops-modal-section">
                    <label className="ops-modal-label" htmlFor="people-admin-cal-notes">
                      Notes (optional)
                    </label>
                    <textarea
                      id="people-admin-cal-notes"
                      className="ops-modal-textarea"
                      rows={2}
                      value={adminCalNotes}
                      onChange={e => setAdminCalNotes(e.target.value)}
                    />
                  </div>
                  <div className="people-hub-form-actions">
                    <button
                      type="button"
                      className="pm-btn pm-btn-primary"
                      disabled={adminCalSubmitting}
                      onClick={() => void submitAdminCalendarEntry()}
                    >
                      {adminCalSubmitting ? 'Adding…' : 'Add to calendar'}
                    </button>
                  </div>
                </div>

                {adminCalendarEntries.length > 0 && (
                  <>
                    <h4 className="people-hub-subpanel-title">Calendar entries you manage</h4>
                    <ul className="people-hub-list">
                      {adminCalendarEntries.map(r => (
                        <li key={r.id} className="people-hub-list-item">
                          <div>
                            <strong>{r.displayName}</strong>
                            <span className="people-hub-muted">
                              {' '}
                              · {format(allDayCalendarDate(r.startDate.toDate()), 'MMM d')} –{' '}
                              {format(allDayCalendarDate(r.endDate.toDate()), 'MMM d, yyyy')} · {r.type}
                            </span>
                            {r.notes?.trim() ? (
                              <div className="people-hub-muted">{r.notes.trim()}</div>
                            ) : null}
                          </div>
                          <div className="people-hub-list-actions">
                            <button
                              type="button"
                              className="pm-btn pm-btn-ghost pm-btn-sm"
                              onClick={() => void removeAdminCalendarEntry(r)}
                            >
                              Remove
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {canOrg && (
              <div className="people-hub-panel">
                <h3 className="people-hub-panel-title">Time off — approvals</h3>
                <p className="people-hub-muted people-hub-tight-top">
                  Admins and executive assistants can approve or reject pending requests (same rules as Firestore
                  security).
                </p>
                {pendingListenerError && (
                  <p className="people-hub-approvals-error" role="alert">
                    Could not load the pending queue: {pendingListenerError}
                  </p>
                )}
                <div className="people-hub-table-wrap pm-scroll">
                  <table className="people-hub-table">
                    <thead>
                      <tr>
                        <th>Who</th>
                        <th>Dates</th>
                        <th>Type</th>
                        <th>Notes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {pendingListenerError ? (
                        <tr>
                          <td colSpan={5} className="people-hub-muted">
                            Fix the error above to load pending requests.
                          </td>
                        </tr>
                      ) : pendingQueue.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="people-hub-muted">
                            No pending requests right now.
                          </td>
                        </tr>
                      ) : (
                        pendingQueue.map(r => (
                          <tr key={r.id}>
                            <td>{r.displayName}</td>
                            <td>
                              {format(allDayCalendarDate(r.startDate.toDate()), 'MMM d')} –{' '}
                              {format(allDayCalendarDate(r.endDate.toDate()), 'MMM d, yyyy')}
                            </td>
                            <td>{r.type}</td>
                            <td className="people-hub-muted">{r.notes || '—'}</td>
                            <td className="people-hub-table-actions">
                              <button
                                type="button"
                                className="pm-btn pm-btn-primary pm-btn-sm"
                                onClick={() => void approve(r, 'approved')}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="pm-btn pm-btn-sm"
                                onClick={() => void approve(r, 'rejected')}
                              >
                                Reject
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
      <PeopleCalDayFlyout state={calDayHover} getInstallWeatherLine={getInstallWeatherLine} />
    </div>
  );
};

export default PeopleHubPage;
